/**
 * vinylpac-utils.js
 * Pure utility functions shared between the configurator (configurator.html)
 * and the Jest unit-test suite.
 *
 * Rules for this file:
 *   - NO DOM access (document, window, localStorage, sessionStorage)
 *   - NO side-effects
 *   - Every function must be independently testable
 *
 * Exports (CommonJS for Jest; also assigned to globalThis for browser use):
 *   escHtml(s)
 *   parseSizeString(sizeText, customW, customH)
 *   buildArtworkFilename(customerName, productName, materialLabel, sizeStr, qty, ext, uid, date)
 *   calculateUnitPrice(qty, sizeMult, matMult, finishMult, spotMult, tiers, markupPct)
 *   getUnitBaseForQty(qty, tiers)
 *   jarLabelTierName(tier)
 *   jarLabelEmbossSummary(tier, finish)
 */

// ── escHtml ─────────────────────────────────────────────────────────────────
/**
 * Escape a string for safe HTML insertion.
 * @param {*} s
 * @returns {string}
 */
function escHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── parseSizeString ──────────────────────────────────────────────────────────
/**
 * Parse a size label into {w, h} in inches.
 * Handles: '4" × 6"', '2" x 2"', 'Custom' (uses customW / customH).
 *
 * @param {string} sizeText  - text from the size <select> option
 * @param {number} customW   - custom width (inches) when sizeText === 'Custom'
 * @param {number} customH   - custom height (inches) when sizeText === 'Custom'
 * @returns {{ w: number, h: number }}
 */
function parseSizeString(sizeText, customW, customH) {
  if (!sizeText || sizeText === 'Custom') {
    return {
      w: parseFloat(customW) || 3,
      h: parseFloat(customH) || 3
    };
  }
  // Match any inch-mark variant (straight, curly, prime) or none, then × or x separator
  var m = sizeText.match(/([\d.]+)["\u2033\u0022\u201c\u201d\u2032\u2019]?\s*[x\xd7]\s*([\d.]+)/i);
  if (m) return { w: parseFloat(m[1]), h: parseFloat(m[2]) };
  return { w: 3, h: 3 };
}

// ── buildArtworkFilename ─────────────────────────────────────────────────────
/**
 * Build the structured artwork filename.
 *
 * Format: Prefix_Product_Material_Size_Qty_YYYYMMDD_uid.ext
 *   e.g.  ORD-1043_MylarBag_Matte_4x6_250_20260415_a3f9.pdf
 *
 * The prefix is typically an order reference from getNextOrderRef() (which simulates
 * GET /admin/api/2025-01/orders.json?limit=1&status=any&fields=order_number).
 * Hyphens and underscores are preserved; all other non-alphanumeric chars are stripped.
 *
 * @param {string} customerName  - order ref or name prefix (e.g. "ORD-1043")
 * @param {string} productName   - e.g. "CUSTOM PACKAGING" or "Mylar Bags"
 * @param {string} materialLabel - e.g. "Matte"
 * @param {string} sizeStr       - e.g. '4" × 6"' or '3" × 3" (custom)'
 * @param {number} qty
 * @param {string} ext           - file extension without dot (e.g. "pdf")
 * @param {string} uid           - 4-char random token (e.g. "a3f9")
 * @param {string} [dateStr]     - YYYYMMDD override (defaults to today)
 * @returns {string}
 */
function buildArtworkFilename(customerName, productName, materialLabel, sizeStr, qty, ext, uid, dateStr) {
  var namePart = (customerName || 'Customer')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_\-]/g, '');
  if (!namePart) namePart = 'Customer';

  var prodPart = (productName || 'Product')
    .replace(/\s+/g, '')
    .replace(/[^A-Za-z0-9]/g, '');

  var matPart = (materialLabel || 'Material')
    .replace(/\s+/g, '')
    .replace(/[^A-Za-z0-9]/g, '');

  var sizePart = (sizeStr || '3x3')
    .replace(/\u00d7/g, 'x')   // × → x before stripping special chars
    .replace(/[^A-Za-z0-9]/g, '');

  var date = dateStr || (function() {
    var d = new Date();
    var mm = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return '' + d.getFullYear() + mm + dd;
  })();

  var uidPart = (uid || Math.random().toString(36).slice(2, 6));
  var extPart = (ext || 'pdf').toLowerCase().replace(/^\./, '');

  return [namePart, prodPart, matPart, sizePart, String(qty), date, uidPart].join('_') + '.' + extPart;
}

// ── getUnitBaseForQty ────────────────────────────────────────────────────────
/**
 * Look up the unit base price for a given quantity from a tiered pricing array.
 * Tiers are sorted ascending; the highest tier the qty qualifies for wins.
 *
 * @param {number} qty
 * @param {Array<{qty:number, unitPrice:number}>} tiers
 * @returns {number}
 */
function getUnitBaseForQty(qty, tiers) {
  if (!tiers || !tiers.length) return 1.70;
  var sorted = tiers.slice().sort(function(a, b) { return a.qty - b.qty; });
  var unit = sorted[0].unitPrice;
  for (var i = 0; i < sorted.length; i++) {
    if (qty >= sorted[i].qty) unit = sorted[i].unitPrice;
    else break;
  }
  return unit;
}

// ── calculateUnitPrice ───────────────────────────────────────────────────────
/**
 * Calculate the final unit price after all multipliers.
 *
 * @param {number} qty
 * @param {number} sizeMult    - multiplier for the selected size
 * @param {number} matMult     - multiplier for the selected material
 * @param {number} finishMult  - 1.08 for matte, 1.0 for glossy
 * @param {number} spotMult    - multiplier for spot finish
 * @param {Array<{qty:number, unitPrice:number}>} tiers
 * @param {number} markupPct   - admin markup percentage (e.g. 20 = 20%)
 * @returns {number}           - unit price in dollars (unformatted)
 */
function calculateUnitPrice(qty, sizeMult, matMult, finishMult, spotMult, tiers, markupPct) {
  var base = getUnitBaseForQty(qty, tiers);
  return base * (sizeMult || 1) * (matMult || 1) * (finishMult || 1) * (spotMult || 1) * (1 + (markupPct || 0) / 100);
}

// ── Jar-label display names ─────────────────────────────────────────────────
function jarLabelTierName(tier) {
  if (tier === 'Economical') return 'Flat';
  if (tier === 'Standard Tier Embossing') return 'Standard';
  if (tier === 'Premium Tier Embossing') return 'Heavy';
  return String(tier || '');
}

function jarLabelEmbossSummary(tier, finish) {
  if (tier === 'Economical' || finish === 'No Embossing') return 'Flat print';
  return jarLabelTierName(tier) + ' embossing' + (finish ? ' · ' + finish : '');
}

// ── Export ───────────────────────────────────────────────────────────────────
// CommonJS for Jest
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { escHtml, parseSizeString, buildArtworkFilename, getUnitBaseForQty, calculateUnitPrice, jarLabelTierName, jarLabelEmbossSummary };
}
// Browser global (already declared as regular functions — available on window)
